import { Head, useForm } from "@inertiajs/react"
import { Check, CreditCard, Zap } from "lucide-react"

import HeadingSmall from "@/components/heading-small"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { billingPortalPath, settingsBillingPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Props {
  plan: "free" | "plus" | "pro"
  has_subscription: boolean
  usage: {
    operations_used: number
    operations_limit: number
    file_retention: string
  }
}

const planDetails = {
  free: {
    name: "Free",
    price: "$0",
    period: "forever",
    features: [
      "5 operations per day",
      "Up to 50MB per file",
      "24-hour file retention",
    ],
  },
  plus: {
    name: "Plus",
    price: "$5",
    period: "/month",
    features: [
      "50 operations per day",
      "Up to 50MB per file",
      "Permanent file storage",
      "Faster processing",
    ],
  },
  pro: {
    name: "Pro",
    price: "$10",
    period: "/month",
    features: [
      "100 operations per day",
      "Up to 50MB per file",
      "Permanent file storage",
      "Batch processing",
      "Priority support",
    ],
  },
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Billing settings",
    href: settingsBillingPath(),
  },
]

export default function Billing({ plan, has_subscription, usage }: Props) {
  const { post, processing } = useForm()
  const details = planDetails[plan]
  const usagePercent = (usage.operations_used / usage.operations_limit) * 100

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Billing | StitchPDF" />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Billing"
            description="Manage your subscription and billing"
          />

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Current Plan
                  </CardTitle>
                  <Badge variant={plan === "free" ? "secondary" : "default"}>
                    {details.name}
                  </Badge>
                </div>
                <CardDescription>
                  <span className="text-foreground text-2xl font-bold">
                    {details.price}
                  </span>
                  <span className="text-muted-foreground">
                    {details.period}
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {details.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Check className="h-4 w-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  {has_subscription ? (
                    <Button
                      onClick={() => post(billingPortalPath())}
                      disabled={processing}
                      className="w-full"
                    >
                      Manage Subscription
                    </Button>
                  ) : (
                    <Button asChild className="w-full">
                      <a href="/pricing">Upgrade Plan</a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Today&apos;s Usage
                </CardTitle>
                <CardDescription>
                  Operations reset daily at midnight
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="mb-2 flex justify-between text-sm">
                    <span>Operations used</span>
                    <span className="font-medium">
                      {usage.operations_used} / {usage.operations_limit}
                    </span>
                  </div>
                  <Progress value={usagePercent} className="h-2" />
                </div>

                <div className="rounded-lg border p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      File retention
                    </span>
                    <span className="font-medium">{usage.file_retention}</span>
                  </div>
                </div>

                {plan === "free" && (
                  <p className="text-muted-foreground text-sm">
                    Need more operations?{" "}
                    <a href="/pricing" className="text-primary underline">
                      Upgrade your plan
                    </a>
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
